/** @jsxImportSource @emotion/react */
'use memo'

import { CalendarOutlined, FileDoneOutlined } from '@ant-design/icons'
import styled from '@emotion/styled'
import { Link } from 'react-router-dom'
import { ROUTES } from '@sportninja/common/constants/app'
import colors from '@sportninja/common/constants/appColors'
import {
  ORG_REGISTRATION_COMPETITION_PLACEHOLDER,
  ORG_REGISTRATION_ORGANIZATION_PLACEHOLDER,
} from '@sportninja/common/features/organization/registration/constants'
import type { OrgRegistration } from '@sportninja/common/features/organization/registration/types'
import type { OrgRegistrationCardModel } from '@sportninja/common/features/organization/registration/utils/cardModel'
import {
  getRegistrationSportArt,
  getRegistrationSportName,
} from '@sportninja/common/features/organization/registration/utils/sportArt'
import { t } from '@sportninja/common/i18n'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import { resolveAssetUrl } from '@sportninja/common/utils/assetUrl'
import {
  captionXSmall,
  heading4,
  paragraphSmall,
  weightMedium,
  weightSemiBold,
} from 'src/components/css'
import SportIcon from 'src/components/icons/SportIcon'
import OrgRegistrationStatusChip from './OrgRegistrationStatusChip'
import OrgRegistrationVenueLink from './OrgRegistrationVenueLink'

interface Props {
  registration: OrgRegistration
  model: OrgRegistrationCardModel
}

/**
 * The card's identity row: competition logo, sport and status chips, name,
 * organization, the season / registration dates and where it is played (each
 * venue opening the map) — over the sport's
 * artwork, so each registration reads as its sport at a glance.
 */
const OrgRegistrationCardHeader = ({ registration, model }: Props) => {
  'use memo'

  const sportName = getRegistrationSportName(registration)

  return (
    <Wrapper
      art={getRegistrationSportArt(registration)}
      isClosed={model.badge === 'past'}
    >
      <Main>
        <Logo
          src={
            resolveAssetUrl(registration.image) ??
            ORG_REGISTRATION_COMPETITION_PLACEHOLDER
          }
          alt=''
        />
        <Titles>
          <Chips>
            {sportName && (
              <SportChip>
                <SportIcon sportName={sportName} size={16} />
                <SportLabel>{sportName}</SportLabel>
              </SportChip>
            )}
            <OrgRegistrationStatusChip
              badge={model.badge}
              label={model.badgeLabel}
            />
          </Chips>
          <Name>
            <NewTabLink
              to={`${ROUTES.SCHEDULE_ROOT}/${registration.scheduleId}`}
              target='_blank'
              rel='noopener noreferrer'
            >
              {registration.name}
            </NewTabLink>
          </Name>
          <Organization
            to={`${ROUTES.ORG_ROOT}/${registration.organizationId}`}
            target='_blank'
            rel='noopener noreferrer'
          >
            <OrganizationLogo
              src={
                resolveAssetUrl(registration.organizationImage) ??
                ORG_REGISTRATION_ORGANIZATION_PLACEHOLDER
              }
              alt=''
            />
            {registration.organizationName}
          </Organization>
        </Titles>
      </Main>
      <Aside>
        <Dates>
          {model.windowLabel && (
            <DateBlock>
              <DateIcon aria-hidden='true'>
                <FileDoneOutlined />
              </DateIcon>
              <DateText>
                <DateLabel>{capitalizeWords('Registration')}</DateLabel>
                <DateValue>{model.windowLabel}</DateValue>
              </DateText>
            </DateBlock>
          )}
          {model.seasonLabel && (
            <DateBlock>
              <DateIcon aria-hidden='true'>
                <CalendarOutlined />
              </DateIcon>
              <DateText>
                {/* 'Competition Start' on SportNinja, 'Season Start' on Canlan. */}
                <DateLabel>
                  {capitalizeWords(`${t('common:competition', {})} start`)}
                </DateLabel>
                <DateValue>{model.seasonLabel}</DateValue>
              </DateText>
            </DateBlock>
          )}
        </Dates>
        {registration.venues.length > 0 && (
          <Venues aria-label={capitalizeWords('Locations')}>
            {registration.venues.map((venue) => (
              <li key={venue.id}>
                <OrgRegistrationVenueLink venue={venue} withPlace />
              </li>
            ))}
          </Venues>
        )}
      </Aside>
    </Wrapper>
  )
}

export default OrgRegistrationCardHeader

const CARD_BACKGROUND = colors.SECONDARY_350

/*
 * Two layers under the content: the sport art on the right (greyscale once
 * the registration has closed), and a scrim that is solid behind the title
 * and fades out to the right so the art shows where nothing is written.
 */
const Wrapper = styled.div<{ art: string; isClosed: boolean }>`
  position: relative;
  isolation: isolate;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 16px 32px;
  padding: 24px;
  background: ${CARD_BACKGROUND};

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    z-index: -2;
    background-image: url(${({ art }) => art});
    background-size: cover;
    background-position: center right;
    background-repeat: no-repeat;
    filter: ${({ isClosed }) => (isClosed ? 'grayscale(1)' : 'none')};
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    z-index: -1;
    background: ${({ isClosed }) =>
      isClosed
        ? `linear-gradient(90deg, ${CARD_BACKGROUND} 0%, ${CARD_BACKGROUND}F2 38%, ${CARD_BACKGROUND}D1 100%)`
        : `linear-gradient(90deg, ${CARD_BACKGROUND} 0%, ${CARD_BACKGROUND}F0 38%, ${CARD_BACKGROUND}BD 66%, ${CARD_BACKGROUND}80 100%)`};
  }
`

/*
 * A small basis, so the dates keep their place on the right and the title
 * column gives up width first; the row only wraps once the title would drop
 * under 240px (phone widths).
 */
const Main = styled.div`
  flex: 999 1 240px;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 16px;
`

const Logo = styled.img`
  width: 72px;
  height: 72px;
  flex: 0 0 72px;
  border-radius: 8px;
  object-fit: cover;
  background: ${colors.SECONDARY_200};
  box-shadow: 0 4px 16px ${colors.BLACK}59;
`

const Titles = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
`

// Solid, so the sport holds up over the brightest artwork. Same box as the
// status chip, so the two always share a height and a centre line.
const SportChip = styled.span`
  ${paragraphSmall}
  ${weightSemiBold}
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 6px;
  box-sizing: border-box;
  height: 24px;
  padding: 0 10px;
  line-height: 1;
  border-radius: 999px;
  white-space: nowrap;

  & > svg {
    display: block;
    flex: 0 0 auto;
  }
  color: ${colors.WHITE};
  background: ${colors.SECONDARY_100};
`

const SportLabel = styled.span`
  display: block;
  text-box: trim-both cap alphabetic;
`

const Name = styled.h2`
  ${heading4}
  margin: 0;
  /* Uppercase-only, so the preset's descender room just reads as a gap. */
  line-height: 1.1;
  color: ${colors.WHITE};
  text-transform: uppercase;
  overflow-wrap: anywhere;
`

// Competition and organization open in a new tab, so the list keeps its
// place, filters and expanded cards.
const NewTabLink = styled(Link)`
  color: inherit;

  &:hover,
  &:focus-visible {
    color: inherit;
    text-decoration: underline;
  }
`

const Organization = styled(NewTabLink)`
  ${paragraphSmall}
  display: flex;
  align-items: center;
  align-self: flex-start;
  gap: 8px;
  color: ${colors.NEUTRAL_50};

  &:hover,
  &:focus-visible {
    color: ${colors.WHITE};
  }
`

const OrganizationLogo = styled.img`
  width: 20px;
  height: 20px;
  border-radius: 4px;
  object-fit: cover;
  background: ${colors.SECONDARY_200};
`

// The dates, with the venues on their own line under them.
const Aside = styled.div`
  flex: 0 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 12px;
`

const Dates = styled.div`
  min-width: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
`

const Venues = styled.ul`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px 24px;
  min-width: 0;
  max-width: 100%;
  margin: 0;
  padding: 0;
  list-style: none;

  & > li {
    min-width: 0;
    max-width: 100%;
  }
`

// A frosted panel, so the dates stay readable over the brightest artwork.
const DateBlock = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid ${colors.SECONDARY_100}99;
  background: ${colors.SECONDARY_400}C7;
  backdrop-filter: blur(6px);
`

const DateIcon = styled.span`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: grid;
  place-items: center;
  font-size: 16px;
  color: ${colors.NEUTRAL_60};
  background: ${colors.SECONDARY_300};
`

const DateText = styled.span`
  display: flex;
  flex-direction: column;
`

const DateLabel = styled.span`
  ${captionXSmall}
  ${weightSemiBold}
  color: ${colors.NEUTRAL_60};
`

const DateValue = styled.span`
  ${paragraphSmall}
  ${weightMedium}
  color: ${colors.WHITE};
`
